import { useState, useCallback } from "react";
import { CardId } from "../types";

interface TouchDragState {
  isDragging: boolean;
  startX: number;
  startY: number;
  currentX: number;
  currentY: number;
  cardId: CardId | null;
}

interface UseTouchDragOptions {
  onDragStart?: (cardId: CardId) => void;
  onDragMove?: (x: number, y: number, cardId: CardId) => void;
  onDragEnd?: (x: number, y: number, cardId: CardId, wasDragging: boolean) => void;
  dragThreshold?: number;
  isEnabled?: boolean;
}

interface TouchDragHandlers {
  onTouchStart: (e: React.TouchEvent, cardId: CardId) => void;
  onTouchMove: (e: React.TouchEvent) => void;
  onTouchEnd: (e: React.TouchEvent, cardId: CardId) => void;
}

interface UseTouchDragReturn {
  touchHandlers: TouchDragHandlers;
  touchDragState: TouchDragState | null;
  isDragging: boolean;
  draggedCardId: CardId | null;
}

export function useTouchDrag({
  onDragStart,
  onDragMove,
  onDragEnd,
  dragThreshold = 10,
  isEnabled = true,
}: UseTouchDragOptions = {}): UseTouchDragReturn {
  const [touchDragState, setTouchDragState] = useState<TouchDragState | null>(null);

  const handleTouchStart = useCallback((e: React.TouchEvent, cardId: CardId) => {
    if (!isEnabled) return;
    
    const touch = e.touches[0];
    const newState = {
      isDragging: false,
      startX: touch.clientX,
      startY: touch.clientY,
      currentX: touch.clientX,
      currentY: touch.clientY,
      cardId,
    };
    
    setTouchDragState(newState);
    onDragStart?.(cardId);
  }, [isEnabled, onDragStart]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isEnabled || !touchDragState) return;
    
    e.preventDefault(); // Prevent scrolling
    const touch = e.touches[0];
    const deltaX = Math.abs(touch.clientX - touchDragState.startX);
    const deltaY = Math.abs(touch.clientY - touchDragState.startY);
    
    // Start dragging if moved more than threshold
    if (!touchDragState.isDragging && (deltaX > dragThreshold || deltaY > dragThreshold)) {
      setTouchDragState(prev => prev ? { ...prev, isDragging: true } : null);
    }
    
    const updatedState = {
      ...touchDragState,
      currentX: touch.clientX,
      currentY: touch.clientY,
      isDragging: touchDragState.isDragging || deltaX > dragThreshold || deltaY > dragThreshold,
    };
    
    setTouchDragState(updatedState);
    
    if (updatedState.isDragging && touchDragState.cardId) {
      onDragMove?.(touch.clientX, touch.clientY, touchDragState.cardId);
    }
  }, [isEnabled, touchDragState, dragThreshold, onDragMove]);

  const handleTouchEnd = useCallback((e: React.TouchEvent, cardId: CardId) => {
    if (!isEnabled) return;
    
    const wasDragging = touchDragState?.isDragging ?? false;
    const finalX = touchDragState?.currentX ?? 0;
    const finalY = touchDragState?.currentY ?? 0;
    
    // Reset state first
    setTouchDragState(null);
    
    // Then call callback
    onDragEnd?.(finalX, finalY, cardId, wasDragging);
  }, [isEnabled, touchDragState, onDragEnd]);

  const touchHandlers: TouchDragHandlers = {
    onTouchStart: handleTouchStart,
    onTouchMove: handleTouchMove,
    onTouchEnd: handleTouchEnd,
  };

  return {
    touchHandlers,
    touchDragState,
    isDragging: touchDragState?.isDragging ?? false,
    draggedCardId: touchDragState?.cardId ?? null,
  };
}